import api from './api';

const isIndependent = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user.type === 'independent_agent';
};

// Bridges the two different backends (group_bookings vs independent_group_bookings)
// behind one API, the same pattern used in leadsService.js - lets
// GroupsManagement/GroupForm/GroupDetails/SecureGroupPaymentModal stay a single
// shared component for both agency and independent agents.
const toRealShape = (g) => ({
  ...g,
  // independent_group_bookings uses group_name/description instead of
  // title/cancellation_policy - normalize to the agency field names the
  // shared components use.
  title: g.title || g.group_name,
  cancellation_policy: g.cancellation_policy ?? g.terms_and_conditions,
});

export const groupBookingsService = {
  getAll: async () => {
    if (isIndependent()) {
      const response = await api.get('/independent/groups');
      return (Array.isArray(response.data) ? response.data : []).map(toRealShape);
    }
    const response = await api.get('/group-bookings');
    return response.data.data || [];
  },

  getById: async (id) => {
    if (isIndependent()) {
      const response = await api.get(`/independent/groups/${id}`);
      return toRealShape(response.data);
    }
    const response = await api.get(`/group-bookings/${id}`);
    return response.data.data || null;
  },

  create: async (groupData) => {
    if (isIndependent()) {
      const response = await api.post('/independent/groups', {
        group_name: groupData.title,
        destination: groupData.destination,
        component_type: groupData.component_type,
        start_date: groupData.start_date,
        end_date: groupData.end_date,
        description: groupData.packages?.[0]?.description || '',
        max_participants: groupData.max_participants,
        price_per_person: groupData.packages?.[0]?.price || 0,
        deposit_per_person: groupData.payment_schedule?.[0]
          ? (groupData.packages?.[0]?.price || 0) * (groupData.payment_schedule[0].percentage / 100)
          : 0,
        inclusions: groupData.packages?.[0]?.inclusions || [],
        terms_and_conditions: groupData.cancellation_policy,
        notes: groupData.notes
      });
      return toRealShape(response.data);
    }
    const response = await api.post('/group-bookings', groupData);
    return response.data.data || null;
  },

  update: async (id, groupData) => {
    if (isIndependent()) {
      const response = await api.put(`/independent/groups/${id}`, {
        group_name: groupData.title,
        destination: groupData.destination,
        component_type: groupData.component_type,
        start_date: groupData.start_date,
        end_date: groupData.end_date,
        max_participants: groupData.max_participants,
        status: groupData.status,
        notes: groupData.notes,
        terms_and_conditions: groupData.cancellation_policy
      });
      return toRealShape(response.data);
    }
    const response = await api.put(`/group-bookings/${id}`, groupData);
    return response.data.data || null;
  },

  addParticipant: async (groupId, participantData) => {
    const endpoint = isIndependent() ? `/independent/groups/${groupId}/participants` : `/group-bookings/${groupId}/participants`;
    const response = await api.post(endpoint, participantData);
    return response.data.data || response.data || null;
  },

  // Agency-only for now (public booking flow / secure payment details aren't
  // wired up for independent agents yet)
  getPublicBooking: async (publicUrl) => {
    const response = await api.get(`/group-bookings/public/${publicUrl}`);
    return response.data.data || null;
  },

  deleteParticipantPayment: async (participantId) => {
    const response = await api.delete(`/group-bookings/participants/${participantId}/payment`);
    return response.data;
  },

  isIndependent
};

export default groupBookingsService;
